import React from 'react';
import { format, set, subDays } from 'date-fns';
import { DAY } from 'Utils/Pagination/usePagination';
import {
} from 'Services/api/stantionsApi';
import { datasourcesSelector, stationsFullSelector } from 'Features/App/Dictionaries/selectors';
import { useSelector } from 'react-redux';
import { useGetDatasourceTypesQuery } from 'Services/api/datasourceTypesApiTS';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { INPUT_DATETIME } from './reportUtils';

export const ReportContext = React.createContext(null);

const initialParams = {
  date: {
    startDate: format(subDays(set(new Date(), { hours: 0, minutes: 0, seconds: 1 }), 7), INPUT_DATETIME),
    endDate: format(new Date(), INPUT_DATETIME),
  },
  calcModuleParams: {
    round: false,
    calc: false,
    includeOffGridData: false,
    factData: false,
  },
  shouldAddByDatasourcePartitionColumns : false,
  pageSize: DAY,
};

const { Provider } = ReportContext;

export default function ReportProvider({ children }) {
  const { data: datasourceTypes = [], isFetching: isLoadingDatasourceTypes } = useGetDatasourceTypesQuery();
  const allStations = useSelector(stationsFullSelector);
  const allDatasources = useSelector(datasourcesSelector);
  const [groups, setGroups] = React.useState([]);
  const [selectedReportId, setSelectedReportId] = React.useState(null);
  const [reportName, setReportName] = React.useState(null);
  const [isFixedSettings, setIsFixedSettings] = React.useState(false);
  const [date, setDate] = React.useState(initialParams.date);
  const [calcModuleParams, setCalcModuleParams] = React.useState(initialParams.calcModuleParams);
  const [pageSize, setPageSize] = React.useState(initialParams.pageSize);

  const [stations = [], datasources = []] = React.useMemo(() => {
    const mappedStations = [];
    const mappedDatasources = [];
    Object.keys(allStations).forEach((stationId) => {
      const station = allStations[stationId];
      if (station.datasourceIds.length > 0) {
        station.datasourceIds.forEach((datasourceId) => {
          const datasource = allDatasources[datasourceId];
          const datasourceType = datasourceTypes.find((dt) => datasource.datasourceTypeId === dt.id);
          mappedDatasources.push({
            id: datasourceId,
            name: datasourceType.fullName,
            shortName: datasourceType.shortName,
            stationIdx: station.station.idx,
            station: station.station.name,
          });
        });
      }
      mappedStations.push({
        id: station.station.id,
        idx: station.station.idx,
        name: station.station.name,
      });
    });
    return [mappedStations, mappedDatasources];
  }, [allDatasources, allStations, datasourceTypes]);

  const posts = React.useMemo(() => {
    const tmp = [];
    groups.forEach((group) => {
      const [, tmpColumns] = group;
      tmpColumns.forEach((column) => {
        let foundedPost = null;
        if (column.sourceType === 'STATION') {
          foundedPost = stations.find((station) => station.id === column.sourceId);
        } else {
          foundedPost = datasources.find((datasource) => datasource.id === column.sourceId);
        }

        if (foundedPost) {
          tmp.push({
            ...foundedPost,
            stationDatasourceName: column.stationDatasourceName
          });
        }
      });
    });
    return [...new Set(tmp)];
  }, [datasources, groups, stations]);

  const columns = React.useMemo(() => {
    const tmp = [];
    groups.forEach((group) => {
      const [, tmpColumns] = group;
      tmpColumns.forEach((column) => {
        tmp.push({
          ...column,
          stationEntityId: column.stationEntity?.id || -1,
        });
      });
    });
    return tmp;
  }, [groups]);

  const reset = React.useCallback(() => {
    setSelectedReportId(null);
    setReportName(null);
    setIsFixedSettings(false);
    setDate(initialParams.date);
    setCalcModuleParams(Object.keys(initialParams.calcModuleParams).includes('factData') ? initialParams.calcModuleParams : { ...initialParams.calcModuleParams, factData: false });
    setPageSize(initialParams.pageSize);
    setGroups([]);
  }, []);

  const value = {
    // new
    groups,
    setGroups,

    // old,
    stations,
    datasources,
    selectedReportId,
    reportName,
    pageSize,
    isFixedSettings,
    date,
    calcModuleParams,
    columns,
    posts,
    setSelectedReportId,
    setIsFixedSettings,
    setDate,
    setCalcModuleParams,
    setReportName,
    setPageSize,
    reset,
  };

  if (isLoadingDatasourceTypes) return <SuspenseSpinner />;

  return (
    <Provider value={value}>
      {children}
    </Provider>
  );
}
